export const replaceUrlStringWithATag = (value) => {
  if (!value) return null
  return value.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank">$1</a>')
}

export const fileDownload = (data, filename, mime = 'text/csv', bom) => {
  const blobData = (typeof bom !== 'undefined') ? [bom, data] : [data]
  const blob = new Blob(blobData, { type: mime || 'application/octet-stream' })
  if (typeof window.navigator.msSaveBlob !== 'undefined') {
    // IE workaround for "HTML7007: One or more blob URLs were
    // revoked by closing the blob for which they were created.
    // These URLs will no longer resolve as the data backing
    // the URL has been freed."
    window.navigator.msSaveBlob(blob, filename)
  } else {
    const blobURL = (window.URL && window.URL.createObjectURL) ? window.URL.createObjectURL(blob) : window.webkitURL.createObjectURL(blob)
    const tempLink = document.createElement('a')
    tempLink.style.display = 'none'
    tempLink.href = blobURL
    tempLink.setAttribute('download', filename)

    // Safari thinks _blank anchor are pop ups. We only want to set _blank
    // target if the browser does not support the HTML5 download attribute.
    // This allows you to download files in desktop safari if pop up blocking
    // is enabled.
    if (typeof tempLink.download === 'undefined') {
      tempLink.setAttribute('target', '_blank')
    }

    document.body.appendChild(tempLink)
    tempLink.click()

    // Fixes "webkit blob resource error 1"
    setTimeout(() => {
      document.body.removeChild(tempLink)
      window.URL.revokeObjectURL(blobURL)
    }, 200)
  }
}

/**
 * 配列の重複を削除します
 * @param {object[]} array 配列
 * @returns ユニーク配列
 */
export const unique = (array) => {
  return [...new Set(array)]
}

/**
 * 文字列をHTMLエスケープします
 * @param {string} str 対象文字列
 * @returns HTMLエスケープされた文字列
 */
export const escapeHtml = (str) => {
  if (typeof str !== 'string') {
    return str
  }
  return str
    .replace(/&/g, '&amp;')
    .replace(/'/g, '&#x27;')
    .replace(/`/g, '&#x60;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * 一定時間待機してから関数を実行
 * @param {function} function_ 関数
 * @param {number} wait 待機時間
 * @param {object} options オプション
 */
export const debounce = (function_, wait = 100, options = {}) => {
  if (typeof function_ !== 'function') {
    throw new TypeError(`Expected the first parameter to be a function, got \`${typeof function_}\`.`)
  }

  if (wait < 0) {
    throw new RangeError('`wait` must not be negative.')
  }

  // TODO: Deprecate the boolean parameter at some point.
  const { immediate } = typeof options === 'boolean' ? { immediate: options } : options

  let storedContext = undefined
  let storedArguments = undefined
  let timeoutId = undefined
  let timestamp = undefined
  let result = undefined

  function later () {
    const last = Date.now() - timestamp

    if (last < wait && last >= 0) {
      timeoutId = setTimeout(later, wait - last)
    } else {
      timeoutId = undefined

      if (!immediate) {
        const callContext = storedContext
        const callArguments = storedArguments
        storedContext = undefined
        storedArguments = undefined
        result = function_.apply(callContext, callArguments)
      }
    }
  }

  const debounced = function (...arguments_) {
    if (storedContext && this !== storedContext) {
      throw new Error('Debounced method called with different contexts.')
    }

    storedContext = this
    storedArguments = arguments_
    timestamp = Date.now()

    const callNow = immediate && !timeoutId

    if (!timeoutId) {
      timeoutId = setTimeout(later, wait)
    }

    if (callNow) {
      const callContext = storedContext
      const callArguments = storedArguments
      storedContext = undefined
      storedArguments = undefined
      result = function_.apply(callContext, callArguments)
    }

    return result
  }

  debounced.clear = () => {
    if (!timeoutId) {
      return
    }

    clearTimeout(timeoutId)
    timeoutId = undefined
  }

  debounced.flush = () => {
    if (!timeoutId) {
      return
    }

    const callContext = storedContext
    const callArguments = storedArguments
    storedContext = undefined
    storedArguments = undefined
    result = function_.apply(callContext, callArguments)

    clearTimeout(timeoutId)
    timeoutId = undefined
  }

  return debounced
}
